import ReportTable from "../ReportTable.component";
import { useEffect, useState, useMemo } from "react";
import ReportModal from "../ReportModal.component";
import ReportService from "../../../services/reports/Report.service";
import BaseReport from "../BaseReport.component";
import { ClassPerformanceRawData, ClassPerformanceParsedData } from "@/src/interfaces/reports/ClassPerformanceReport.interface";
import TipContext from "../../../providers/GlobalTip";

interface ClassPerformanceReportProps {
    clientId: string | number;
    epochMonth: number | null;
}

const ClassPerformanceReport = ({ clientId, epochMonth }: ClassPerformanceReportProps) => {
    const globalTip = TipContext.useMessage();
    const [data, setData] = useState<ClassPerformanceRawData[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            ReportService.getClassPerformanceReportData({ clientId, epochMonth }).then((response) => {
                if (response) {
                    setData(response);
                } else {
                    setData([]);
                }
                setLoading(false);
            }).catch((error) => {
                if (globalTip)
                    globalTip.show("Failed to fetch data");
                setLoading(false);
            });
        };
        fetchData();
    }, [clientId, epochMonth]);

    const parsedData = useMemo(() => {
        console.log(data);
        if (!data || data.length === 0) return [];
    
        // Calculate totals
        const totalSales = data.reduce((sum, item) => sum + (item.sales || 0), 0);
        const totalInventory = data.reduce((sum, item) => sum + (item.inventory || 0), 0);
        const totalInflowRetail = data.reduce((sum, item) => sum + (item.inflowRetail || 0), 0);
        const totalInflowCost = data.reduce((sum, item) => sum + (item.inflowCost || 0), 0);
        const totalMarkdown = data.reduce((sum, item) => sum + (item.markdowns || 0), 0);

        // Group by location
        const locations = data.reduce((acc, item) => {
            if (!acc[item.locationCode]) {
                acc[item.locationCode] = {
                    locationCode: item.locationCode,
                    locationName: item.locationName,
                    classes: [],
                    totalSales: 0,
                    totalInventory: 0,
                    totalInflowRetail: 0,
                    totalInflowCost: 0,
                    totalMarkdown: 0,
                };
            }
            acc[item.locationCode].classes.push(item);
            acc[item.locationCode].totalSales += item.sales || 0;
            acc[item.locationCode].totalInventory += item.inventory || 0;
            acc[item.locationCode].totalInflowRetail += item.inflowRetail || 0;
            acc[item.locationCode].totalInflowCost += item.inflowCost || 0;
            acc[item.locationCode].totalMarkdown += item.markdowns || 0;
            return acc;
        }, {} as Record<string, any>);
    
        // Parse data for each class
        const result: ClassPerformanceParsedData[] = [];
    
        const markdownPercent = (totalSales && totalMarkdown) ? (totalMarkdown / totalSales) : 0;

        // Add total row first
        result.push({
            locationCode: "",
            locationName: "Total",
            classCode: "",
            className: "",
            sales: ReportService.formatCurrency(totalSales),
            salesPercentToGroup: "",
            salesPercentToTotal: "",
            inventory: ReportService.formatCurrency(totalInventory),
            inventoryPercentToGroup: "",
            inventoryPercentToTotal: "",
            inflowRetail: ReportService.formatCurrency(totalInflowRetail),
            inflowRetailPercentToGroup: "",
            inflowRetailPercentToTotal: "",
            inflowCost: ReportService.formatCurrency(totalInflowCost),
            cop: ReportService.formatPercent(ReportService.calculateCop({ inflowCost: totalInflowCost, sales: totalSales }) * 100),
            mmu: ReportService.formatPercent(ReportService.calculateMmu({ inflowRetail: totalInflowRetail, inflowCost: totalInflowCost, markdownPercent: markdownPercent })  * 100),
            imu: ReportService.formatPercent(ReportService.calculateImu({ inflowRetail: totalInflowRetail, inflowCost: totalInflowCost }) * 100),
            markdownPercentage: ReportService.formatPercent(markdownPercent * 100),
            isLocationTotal: true
        });
    
        // Sort locations by locationCode in ASC order
        const sortedLocations = Object.values(locations).sort((a: any, b: any) => 
            a.locationCode.localeCompare(b.locationCode)
        );
    
        // Add data for each location
        sortedLocations.forEach((location: any) => {
            const markdownPercent = (location.sales ? location.markdown / location.sales : 0) || 0;

            // Add location total row
            result.push({
                locationCode: location.locationCode,
                locationName: location.locationName,
                classCode: "",
                className: "Total",
                sales: ReportService.formatCurrency(location.totalSales),
                salesPercentToGroup: "",
                salesPercentToTotal: ReportService.formatPercent((location.totalSales / totalSales) * 100),
                inventory: ReportService.formatCurrency(location.totalInventory),
                inventoryPercentToGroup: "",
                inventoryPercentToTotal: ReportService.formatPercent((location.totalInventory / totalInventory) * 100),
                inflowRetail: ReportService.formatCurrency(location.totalInflowRetail),
                inflowRetailPercentToGroup: "",
                inflowRetailPercentToTotal: ReportService.formatPercent((location.totalInflowRetail / totalInflowRetail) * 100),
                inflowCost: ReportService.formatCurrency(location.totalInflowCost),
                cop: ReportService.formatPercent(ReportService.calculateCop({ inflowCost: location.totalInflowCost, sales: location.totalSales }) * 100),
                mmu: ReportService.formatPercent(ReportService.calculateMmu({ inflowRetail: location.totalInflowRetail, inflowCost: location.totalInflowCost, markdownPercent: markdownPercent }) * 100),
                imu: ReportService.formatPercent(ReportService.calculateImu({ inflowRetail: location.totalInflowRetail, inflowCost: location.totalInflowCost }) * 100),
                markdownPercentage: ReportService.formatPercent(markdownPercent * 100),
                isLocationTotal: true
            });
    
            // Sort classes by classCode in ASC order
            const sortedClasses = [...location.classes].sort((a: ClassPerformanceRawData, b: ClassPerformanceRawData) => 
                a.classCode.localeCompare(b.classCode)
            );
    
            // Add rows for each class in the location
            sortedClasses.forEach((classItem: ClassPerformanceRawData) => {
                const sales = classItem.sales || 0;
                const inventory = classItem.inventory || 0;
                const inflowRetail = classItem.inflowRetail || 0;
                const inflowCost = classItem.inflowCost || 0;
                const markdown = classItem.markdowns || 0;
                const markdownPercent = (sales && markdown) ? (markdown / sales) : 0;
    
                result.push({
                    locationCode: classItem.locationCode,
                    locationName: "", // Blank location name for subrows
                    classCode: classItem.classCode,
                    className: classItem.className,
                    sales: ReportService.formatCurrency(sales),
                    salesPercentToGroup: ReportService.formatPercent((sales / location.totalSales) * 100),
                    salesPercentToTotal: ReportService.formatPercent((sales / totalSales) * 100),
                    inventory: ReportService.formatCurrency(inventory),
                    inventoryPercentToGroup: ReportService.formatPercent((inventory / location.totalInventory) * 100),
                    inventoryPercentToTotal: ReportService.formatPercent((inventory / totalInventory) * 100),
                    inflowRetail: ReportService.formatCurrency(inflowRetail),
                    inflowRetailPercentToGroup: ReportService.formatPercent((inflowRetail / location.totalInflowRetail) * 100),
                    inflowRetailPercentToTotal: ReportService.formatPercent((inflowRetail / totalInflowRetail) * 100),
                    inflowCost: ReportService.formatCurrency(inflowCost),
                    cop: ReportService.formatPercent(ReportService.calculateCop({ inflowCost, sales }) * 100),
                    mmu: ReportService.formatPercent(ReportService.calculateMmu({ inflowRetail, inflowCost, markdownPercent }) * 100),
                    imu: ReportService.formatPercent(ReportService.calculateImu({ inflowRetail, inflowCost }) * 100),
                    markdownPercentage: ReportService.formatPercent(markdownPercent * 100),
                    isLocationTotal: false
                });
            });
        });
    
        return result;
    }, [data]);

    const columns = [
        { name: "Location", sortable: false, customClassName: "px-4 py-2 bg-gray-500 text-white" },
        { name: "Class", sortable: false, customClassName: "px-4 py-2 bg-purple-100 border-purple-700 text-purple-800" },
        { name: "Sales", sortable: false },
        { name: "% of Location", sortable: false },
        { name: "% of Total", sortable: false },
        { name: "Inventory", sortable: false },
        { name: "% of Location", sortable: false },
        { name: "% of Total", sortable: false },
        { name: "Inflow Retail", sortable: false },
        { name: "% of Location", sortable: false },
        { name: "% of Total", sortable: false },
        { name: "Inflow Cost", sortable: false },
        { name: "COP%", sortable: false },
        { name: "IMU", sortable: false },
        { name: "MKDNS %", sortable: false },
        { name: "MMU", sortable: false },
    ];

    const rowClassName = (rowIndex: number) => {
        const row = parsedData[rowIndex];
        if (row?.locationName === "Total") return "font-bold bg-tw-primary text-white hover:bg-primary-light";
        return null;
    };

    const tableData = useMemo(() => {
        return parsedData.map((item, index) => {
            // Format the location column as "locationCode locationName"
            let locationDisplay = "";
            if (item.locationName === "Total") {
                locationDisplay = "Total";
            } else if (item.className === "Total") {
                // Only show location name/code for the location total row
                locationDisplay = `${item.locationCode} ${item.locationName}`;
            }
            // All other rows (class rows) have blank location
            
            // Format the class column as "classCode className"
            let classDisplay = item.className;
            if (item.classCode && item.className !== "Total") {
                classDisplay = `${item.classCode} ${item.className}`;
            }
            
            return [
                locationDisplay,
                classDisplay,
                item.sales,
                item.salesPercentToGroup,
                item.salesPercentToTotal,
                item.inventory,
                item.inventoryPercentToGroup,
                item.inventoryPercentToTotal,
                item.inflowRetail,
                item.inflowRetailPercentToGroup,
                item.inflowRetailPercentToTotal,
                item.inflowCost,
                item.cop,
                item.imu,
                item.markdownPercentage,
                item.mmu,
            ];
        });
    }, [parsedData]);

    return (
        <div className="w-full h-full flex flex-col items-center justify-center text-center">
            <h2 className="text-2xl font-bold mb-4">Class Performance Report</h2>
            {loading ? (
                <p>Loading...</p>
            ) : (
                <div className="w-full h-full">
                    {/* BaseReport for PDF/CSV generation */}
                    <BaseReport
                        title="Class Performance Report"
                        tableColumns={columns.map((col) => col.name)}
                        tableData={tableData}
                        fileName={`Class_Performance_Report_${new Date().toLocaleDateString()}`}
                    />
                    
                    {/* Display table with export buttons */}
                    <ReportTable
                        columns={columns}
                        data={tableData}
                        rowClassName={rowClassName}
                        defaultSortColumn="Sales"
                        defaultSortDirection="desc"
                        headerClassName="bg-slate-700 !text-white !text-center py-2 px-auto"
                        toPdf={() => (window as any).BaseReportExport && (window as any).BaseReportExport('pdf')}
                        toCsv={() => (window as any).BaseReportExport && (window as any).BaseReportExport('csv')}
                    />
                </div>
            )}
        </div>
    );
};

interface ClassPerformanceReportModalProps {
    clientId: string | number;
    epochMonth: number | null;
    onClose: () => void;
}

const ClassPerformanceReportModal = ({ clientId, epochMonth, onClose }: ClassPerformanceReportModalProps) => {
    return (
        <ReportModal
            onClose={onClose}
        >
            <ClassPerformanceReport clientId={clientId} epochMonth={epochMonth} />
        </ReportModal>
    );
};

export {
    ClassPerformanceReport,
    ClassPerformanceReportModal
}